import { ref, computed } from "vue";
import { defineStore, storeToRefs } from "pinia";
import { useSectionsStore } from "./sections";
import { useCardsStore } from "./cards";
import genToken from "@utils/generateToken";

export const useSurveySectionsStore = defineStore("surveySections", () => {
  const sectionsStore = useSectionsStore();
  const cardsStore = useCardsStore();
  const { sections } = storeToRefs(sectionsStore);
  const { cards } = storeToRefs(cardsStore);

  const selectedCardTokens = ref([]);
  const collapsedSectionsTokens = ref({});

  // getters
  const cardsBySection = computed(() =>
    sections.value.map((section) => cards.value.filter((card) => card.section_token === section.token)),
  );

  const numberOfSelectedCards = computed(() => selectedCardTokens.value.length);

  const sectionIndex = (sectionToken) => sections.value.findIndex((section) => section.token === sectionToken);

  const cardsInSection = (sectionToken) => cardsBySection.value[sectionIndex(sectionToken)] ?? [];

  const lastTokenBeforeSection = (sectionToken) =>
    cardsBySection.value.slice(0, sectionIndex(sectionToken)).flat().at(-1)?.token ?? null;

  const isCollapsedSection = (sectionToken) => collapsedSectionsTokens.value[sectionToken] != null;
  const isCardMultiSelect = (cardToken) => selectedCardTokens.value.includes(cardToken);
  const resetSelectedCards = () => (selectedCardTokens.value = []);

  // actions
  const defaultSectionName = () => {
    let number = sections.value.length + 1;
    while (sections.value.some((section) => section.name === `Section ${number}`)) number++;
    return `Section ${number}`;
  };

  const toggleCollapsedSection = ({ token }) => {
    if (collapsedSectionsTokens.value[token] != null) {
      delete collapsedSectionsTokens.value[token];
    } else {
      collapsedSectionsTokens.value[token] = true;
    }
  }

  const expandSectionIncludingCard = (cardToken) => {
    const card = cardsStore.findByToken(cardToken);
    if (card?.section_token) delete collapsedSectionsTokens.value[card.section_token];
  }

  const toggleCardSelection = (cardToken) => {
    const index = selectedCardTokens.value.indexOf(cardToken);
    if (index >= 0) selectedCardTokens.value.splice(index, 1);
    else selectedCardTokens.value.push(cardToken);
  }

  const createSection = ({ token }) => {
    const anchor = cardsStore.findByToken(token);
    if (!anchor) return;

    const newSection = { token: genToken(), name: defaultSectionName() };
    sectionsStore.addSection(newSection, anchor.section_token);

    const trailing = cards.value.filter(
      (card) => card.section_token === anchor.section_token && cardsStore.findIndex(card.token) > cardsStore.findIndex(token),
    );
    let previous = token;
    trailing.forEach((card) => {
      cardsStore.moveCard(card.token, previous, newSection.token);
      previous = card.token;
    });
  }

  const removeSection = ({ token }) => {
    const index = sections.value.findIndex((section) => section.token === token);
    if (index <= 0) return;

    const previous = sections.value[index - 1];
    cards.value.forEach((card) => {
      if (card.section_token !== token) return;
      card.section_token = previous.token;
      card.section_id = undefined;
    });
    sectionsStore.removeSection(token);
  }

  const setSectionName = ({ token }, name) => {
    sectionsStore.renameSection(token, name);
  }

  const dragCardFromSection = (cardToken) => {
    if (!selectedCardTokens.value.includes(cardToken)) selectedCardTokens.value = [cardToken];
    return true;
  }

  const dropCardsIntoSection = (targetSectionToken, indexInSection) => {
    const layoutOrder = cardsBySection.value.flat();
    const layoutIndex = (token) => layoutOrder.findIndex((card) => card.token === token);
    const movedTokens = [...selectedCardTokens.value].sort((a, b) => layoutIndex(a) - layoutIndex(b));

    const remaining = cardsInSection(targetSectionToken).filter((card) => !movedTokens.includes(card.token));
    const previous = indexInSection > 0 ? (remaining[indexInSection - 1] ?? remaining.at(-1)) : null;
    let anchorToken = previous?.token ?? lastTokenBeforeSection(targetSectionToken);

    movedTokens.forEach((token) => {
      cardsStore.moveCard(token, anchorToken, targetSectionToken);
      anchorToken = token;
    });
    resetSelectedCards();
    return movedTokens;
  }

  const dropSection = (newIndex) => {
    const section = sections.value[newIndex];
    if (!section) return;
    sectionsStore.moveSection(section.token, sections.value[newIndex - 1]?.token ?? null);
    cardsStore.resortBySections(sections.value.map(({ token }) => token));
  }

  return {
    // state
    sections,
    selectedCardTokens,

    // actions
    toggleCollapsedSection,
    expandSectionIncludingCard,
    toggleCardSelection,
    createSection,
    removeSection,
    setSectionName,
    dragCardFromSection,
    dropCardsIntoSection,
    dropSection,
    isCardMultiSelect,
    isCollapsedSection,
    resetSelectedCards,

    // getters
    cardsBySection,
    numberOfSelectedCards,
  };
});
